import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import { AppServices } from 'middleware'
import { endpoints as articleEndpoints, invalidatePLCache } from 'products/reduxApi'
import { createQueryResponseHandling } from 'utils/errorHandler'
import { hasFeature } from 'features/hocs'
import { State } from 'store'
import { CapacityDetails, CapacitySummary } from './types'
import {
    CapacityList,
    CapacityMemberCounts,
    CapacityMembers,
    SavePlan,
    SaveResult,
    splitVisitorCapacities,
    TodayUsage,
    VisitorKind,
} from './api/capacityGateway'
import { OptionItemSweep, sweepOptionItems } from './api/clientSideGateway'
import { V2ResolvedTimeSlots } from './api/capacityV2Service'
import { CAPACITY_SERVER_FLAGS, CapacityServerFlags, createCapacityGateway } from './api/createCapacityGateway'

export const capacityApi = createApi({
    reducerPath: 'capacityApi',
    tagTypes: ['Capacities', 'Capacity', 'CapacityMembers', 'TodayUsage', 'OptionItemSweep'],
    baseQuery: fetchBaseQuery({}),
    endpoints: () => ({}),
})

export const serverFlagsFromState = (state: State, accountSlug: string): CapacityServerFlags => {
    const conviousAdmin = state.auth.user?.isAdmin ?? false
    const read = (featureName: string) =>
        hasFeature({ accountSlug, features: state.features, featureName, conviousAdmin })
    return {
        list: read(CAPACITY_SERVER_FLAGS.list),
        members: read(CAPACITY_SERVER_FLAGS.members),
        save: read(CAPACITY_SERVER_FLAGS.save),
        delete: read(CAPACITY_SERVER_FLAGS.delete),
        health: read(CAPACITY_SERVER_FLAGS.health),
        usage: read(CAPACITY_SERVER_FLAGS.usage),
        visitors: read(CAPACITY_SERVER_FLAGS.visitors),
        timeSlots: read(CAPACITY_SERVER_FLAGS.timeSlots),
    }
}

type BaseQueryApi = Parameters<BaseQueryFn>[1]

const optionItemsApi = capacityApi.injectEndpoints({
    endpoints: (builder) => ({
        getOptionItemSweep: builder.query<OptionItemSweep, string>({
            providesTags: ['OptionItemSweep'],
            keepUnusedDataFor: 300,
            queryFn: (accountSlug) =>
                createQueryResponseHandling(() => sweepOptionItems(AppServices.optionsService, accountSlug)),
        }),
    }),
})

const gatewayFor = (api: BaseQueryApi, accountSlug: string) =>
    createCapacityGateway(
        {
            inventoryService: AppServices.inventoryService,
            articleService: AppServices.articleService,
            capacityReportService: AppServices.capacityReportService,
            capacityV2: AppServices.capacityV2Service,
            loadArticles: async (slug) => {
                const subscription = api.dispatch(articleEndpoints.getAllArticles.initiate({ account: slug }))
                try {
                    return await subscription.unwrap()
                } finally {
                    subscription.unsubscribe()
                }
            },
            loadOptionItems: async (slug) => {
                const subscription = api.dispatch(optionItemsApi.endpoints.getOptionItemSweep.initiate(slug))
                try {
                    return await subscription.unwrap()
                } finally {
                    subscription.unsubscribe()
                }
            },
        },
        serverFlagsFromState(api.getState() as State, accountSlug),
    )

const extendedApi = capacityApi.injectEndpoints({
    endpoints: (builder) => ({
        getCapacities: builder.query<CapacitySummary[], string>({
            providesTags: (result) => [
                'Capacities',
                ...(result ?? []).map((capacity) => ({ type: 'Capacity' as const, id: capacity.id })),
            ],
            queryFn: (accountSlug, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).listCapacities(accountSlug)),
        }),
        getCapacity: builder.query<CapacityDetails, { accountSlug: string; capacityId: string }>({
            providesTags: (_, __, { capacityId }) => [{ type: 'Capacity', id: capacityId }],
            queryFn: ({ accountSlug, capacityId }, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).getCapacity(accountSlug, capacityId)),
        }),
        getCapacityMembers: builder.query<CapacityMembers, { accountSlug: string; capacityId: string }>({
            providesTags: (_, __, { capacityId }) => [{ type: 'CapacityMembers', id: capacityId }],
            queryFn: ({ accountSlug, capacityId }, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).getMembers(accountSlug, capacityId)),
        }),
        // The list page reads visitors and capacities from one request, so both sections load, fail and
        // retry together and no row moves between them after the fact.
        getCapacityList: builder.query<CapacityList, string>({
            providesTags: (result) => [
                'Capacities',
                ...(result?.capacities ?? []).map((capacity) => ({ type: 'Capacity' as const, id: capacity.id })),
            ],
            queryFn: (accountSlug, api) =>
                createQueryResponseHandling(async () => {
                    const gateway = gatewayFor(api, accountSlug)
                    const capacities = await gateway.listCapacities(accountSlug)
                    return splitVisitorCapacities(capacities, (capacity) => gateway.visitorKindOf(capacity))
                }),
        }),
        createVisitorCapacity: builder.mutation<CapacitySummary, { accountSlug: string; kind: VisitorKind }>({
            invalidatesTags: ['Capacities'],
            queryFn: ({ accountSlug, kind }, api) =>
                createQueryResponseHandling(() =>
                    gatewayFor(api, accountSlug).createVisitorCapacity(accountSlug, kind),
                ),
        }),
        getCapacityMemberCounts: builder.query<CapacityMemberCounts, string>({
            providesTags: ['CapacityMembers'],
            queryFn: (accountSlug, api) =>
                createQueryResponseHandling(() => gatewayFor(api, accountSlug).getMemberCounts(accountSlug)),
        }),
        getProductTimeSlots: builder.query<V2ResolvedTimeSlots | null, { accountSlug: string; productId: string }>({
            keepUnusedDataFor: 60,
            queryFn: ({ accountSlug, productId }) =>
                createQueryResponseHandling(() =>
                    AppServices.capacityV2Service.productTimeSlots(accountSlug, productId),
                ),
        }),
        getTodayUsage: builder.query<
            Record<string, TodayUsage>,
            { accountSlug: string; capacityIds: string[]; date: string }
        >({
            providesTags: (_, __, { capacityIds }) => capacityIds.map((id) => ({ type: 'TodayUsage' as const, id })),
            keepUnusedDataFor: 60,
            queryFn: ({ accountSlug, capacityIds, date }, api) =>
                createQueryResponseHandling(() =>
                    gatewayFor(api, accountSlug).getTodayUsage(accountSlug, capacityIds, date),
                ),
        }),
        saveCapacity: builder.mutation<SaveResult, { accountSlug: string; plan: SavePlan }>({
            invalidatesTags: (result) =>
                result?.capacityId
                    ? [
                          'Capacities',
                          { type: 'Capacity', id: result.capacityId },
                          { type: 'CapacityMembers', id: result.capacityId },
                          'CapacityMembers',
                          { type: 'TodayUsage', id: result.capacityId },
                      ]
                    : ['Capacities'],
            queryFn: async ({ accountSlug, plan }, api) => {
                const response = await createQueryResponseHandling(() =>
                    gatewayFor(api, accountSlug).save(accountSlug, plan),
                )
                if (response.data && plan.productOps.length > 0) {
                    invalidatePLCache(api.dispatch, accountSlug)
                    const refresh = api.dispatch(
                        articleEndpoints.getAllArticles.initiate({ account: accountSlug }, { forceRefetch: true }),
                    )
                    try {
                        await refresh.unwrap()
                    } catch {
                    } finally {
                        refresh.unsubscribe()
                    }
                }
                return response
            },
        }),
    }),
})

export const { reducer, reducerPath } = capacityApi
export const capacityApiUtil = extendedApi.util
export const {
    useGetCapacitiesQuery,
    useGetCapacityQuery,
    useGetCapacityMembersQuery,
    useGetCapacityMemberCountsQuery,
    useGetCapacityListQuery,
    useCreateVisitorCapacityMutation,
    useGetTodayUsageQuery,
    useGetProductTimeSlotsQuery,
    useSaveCapacityMutation,
} = extendedApi
